import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Button, Card, Badge } from "../components/kit";

export default function Solutions() {
  const navigate = useNavigate();

  const products = [
    {
      mark: "vitals",
      name: "Vitals",
      brand: "vitals",
      for: "For business owners",
      headline: "From buried in the day-to-day to seeing the business from outside it.",
      body: "Vitals is the same underlying engine, offered directly to founders who don't have a coach — or who want their own standalone view alongside one. It's the tool that helps a founder graduate from hustler to founder to CEO. A founder in high-growth mode doesn't have time to manually log data, so Vitals works like a sensor: a self-reported baseline today, growing into a fifteen-second daily habit, and built to connect to your sales and accounting systems later.",
      points: [
        "Free for every business owner — no trial, no card, no payment step",
        "A guided 25-question baseline across seven areas of business health",
        "A daily check-in of three rotating questions — under fifteen seconds",
        "One Business Health Score, with streaks, badges, and progress you can watch move",
        "Anonymous benchmarking against a peer cohort of similar businesses",
      ],
      cta: "Start your free baseline",
      onCta: () => navigate("/register"),
    },
    {
      mark: "catalyst",
      name: "Catalyst",
      brand: "catalyst",
      for: "For business coaches",
      headline: "Personalize the relationship again — at the scale of hundreds of clients.",
      body: "Catalyst is a white-label engagement and benchmarking tool for business coaches whose clients number in the hundreds and are served through large-scale events and asynchronous content. At that scale, content and events alone can't personalize a relationship. Catalyst gives coaches cohort visibility built on real, sustained data — not survey responses that go stale. It's white-label by design: the coach's brand stays in front of clients, Catalyst is the infrastructure underneath, the way Stripe or Twilio power a product without becoming the visible brand.",
      points: [
        "Anonymized benchmarks within your cohort and across broader business communities",
        "Challenges built on real revenue, profitability, and growth data",
        "Growth metrics you can customize beyond the baseline set",
        "White-label throughout — your brand, your relationship, backed by real numbers",
        "Priced by active members — start free with up to 25, scale to thousands",
      ],
      cta: "See Catalyst plans",
      onCta: () => navigate("/pricing"),
    },
  ];

  return (
    <div data-testid="solutions-page">
      <section className="buktu-wrap" style={{ paddingTop: "var(--space-20)", paddingBottom: "var(--space-10)" }}>
        <div style={{ maxWidth: 720 }} className="buktu-rise">
          <span className="buktu-eyebrow">Solutions</span>
          <h1 style={{ font: "var(--type-display)", fontSize: "clamp(32px, 5vw, 49px)", letterSpacing: "var(--tracking-tight)", margin: "var(--space-4) 0 var(--space-5)" }}>
            One shared engine, two front doors.
          </h1>
          <p style={{ font: "var(--type-body)", fontSize: 18, color: "var(--text-muted)" }}>
            Most tools built for business owners rely on self-reported surveys that go stale and get abandoned. Buktu is built around sustained data instead — a live, honest read on how a business is actually doing. Vitals serves owners directly, free. Catalyst puts that same intelligence in the hands of the coaches who serve them.
          </p>
        </div>
      </section>

      <section className="buktu-wrap" style={{ display: "flex", flexDirection: "column", gap: "var(--space-8)", paddingBottom: "var(--space-10)" }}>
        {products.map((p, i) => (
          <Card key={p.name} padding="0" data-brand={p.brand} data-testid={`solution-${p.name.toLowerCase()}`} style={{ overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: i % 2 === 0 ? "0.9fr 1.1fr" : "1.1fr 0.9fr", gap: 0 }} className="buktu-sol-grid">
              {/* accent panel */}
              <div
                style={{
                  order: i % 2 === 0 ? 0 : 1,
                  background: "var(--accent-product-soft)",
                  padding: "var(--space-10)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--space-4)",
                  justifyContent: "center",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
                  <img src={`/brand/${p.mark}-mark.svg`} alt="" style={{ width: 44, height: 44 }} />
                  <span style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 34, letterSpacing: "-0.03em", color: "var(--buktu-green)" }}>{p.name.toLowerCase()}</span>
                </div>
                <Badge tone="accent">{p.for}</Badge>
                <h2 style={{ font: "var(--type-title)", letterSpacing: "var(--tracking-snug)", color: "var(--text-strong)" }}>{p.headline}</h2>
              </div>

              {/* copy panel */}
              <div style={{ padding: "var(--space-10)", display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
                <p style={{ font: "var(--type-body)", color: "var(--text-body)" }}>{p.body}</p>
                <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
                  {p.points.map((pt) => (
                    <li key={pt} style={{ display: "flex", gap: "var(--space-3)", font: "var(--type-body-sm)", color: "var(--text-body)" }}>
                      <span style={{ width: 22, height: 22, borderRadius: 999, flexShrink: 0, background: "var(--surface-brand-wash)", color: "var(--buktu-green)", display: "grid", placeItems: "center", marginTop: 1 }}><Check size={13} /></span>
                      {pt}
                    </li>
                  ))}
                </ul>
                <div>
                  <Button data-testid={`solution-${p.name.toLowerCase()}-cta`} onClick={p.onCta} iconRight={<ArrowRight size={16} />}>{p.cta}</Button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </section>

      <style>{`
        @media (max-width: 860px) {
          .buktu-sol-grid { grid-template-columns: 1fr !important; }
          .buktu-sol-grid > div:first-child { order: 0 !important; }
        }
      `}</style>
    </div>
  );
}
